/**
 * Header — main navigation bar (HandiPrêt branding)
 *
 * Glassmorphism on scroll, gradient CTA button,
 * responsive mobile drawer. Matches lea_asso_website style.
 */

"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { useState, useEffect } from "react";
import { Menu, Plus, User, LogIn, ArrowRight, MessageSquare, LogOut, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
} from "@/components/ui/sheet";
import { NAV_LINKS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/layout/theme-toggle";

export function Header() {
    const pathname = usePathname();
    const { data: session } = useSession();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
                scrolled
                    ? "glass shadow-hp"
                    : "bg-white/95 dark:bg-hp-dark/95 backdrop-blur-sm"
            )}
        >
            <div className="container mx-auto flex h-20 items-center justify-between px-4">
                {/* Logo */}
                <Link
                    href="/"
                    className="group flex items-center gap-3"
                    aria-label="HandiPrêt — Accueil"
                >
                    <Image
                        src="/logo-handipret.png"
                        alt="HandiPrêt"
                        width={60}
                        height={42}
                        style={{ height: 'auto' }}
                        className="transition-transform group-hover:scale-105"
                        priority
                    />
                </Link>

                {/* Desktop nav */}
                <nav
                    className="hidden items-center gap-1 lg:flex"
                    aria-label="Navigation principale"
                >
                    {NAV_LINKS.map((link) => {
                        const isActive = pathname === link.href;
                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    "relative rounded-lg px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                                    isActive
                                        ? "text-hp-purple"
                                        : "text-hp-gray hover:text-hp-purple"
                                )}
                            >
                                {link.label}
                                {isActive && (
                                    <span className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-gradient-to-r from-hp-purple to-hp-orange" />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Actions */}
                <div className="flex items-center gap-3">
                    <ThemeToggle />

                    {session ? (
                        <>
                            <Button asChild size="icon" variant="ghost" className="text-hp-gray hover:text-hp-purple">
                                <Link href="/messages" aria-label="Messages">
                                    <MessageSquare className="h-5 w-5" />
                                </Link>
                            </Button>

                            <Button
                                asChild
                                size="sm"
                                className="hidden bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark hover:shadow-hp-hover sm:inline-flex"
                            >
                                <Link href="/annonces/nouvelle">
                                    <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                                    Proposer du matériel
                                </Link>
                            </Button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-hp-gray hover:text-hp-purple" aria-label="Mon compte">
                                        <User className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem asChild>
                                        <Link href="/profil" className="flex items-center gap-2">
                                            <User className="h-4 w-4" /> Mon profil
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link href="/profil/parametres" className="flex items-center gap-2">
                                            <Settings className="h-4 w-4" /> Paramètres
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => signOut({ callbackUrl: "/" })}
                                        className="text-red-600 dark:text-red-400 flex items-center gap-2 cursor-pointer"
                                    >
                                        <LogOut className="h-4 w-4" /> Déconnexion
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <Button
                            asChild
                            size="sm"
                            className="bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark hover:shadow-hp-hover"
                        >
                            <Link href="/auth/login">
                                <LogIn className="mr-1 h-4 w-4" aria-hidden="true" />
                                Connexion
                                <ArrowRight className="ml-1 h-3 w-3" aria-hidden="true" />
                            </Link>
                        </Button>
                    )}

                    {/* Mobile menu */}
                    <Sheet>
                        <SheetTrigger asChild className="lg:hidden">
                            <Button variant="ghost" size="icon" aria-label="Ouvrir le menu" className="text-hp-gray">
                                <Menu className="h-6 w-6" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-80">
                            <div className="flex items-center gap-3 mb-8">
                                <Image
                                    src="/logo-handipret.png"
                                    alt="HandiPrêt"
                                    width={48}
                                    height={34}
                                    style={{ height: 'auto' }}
                                />
                                <SheetTitle className="text-lg font-bold gradient-text">
                                    HandiPrêt
                                </SheetTitle>
                            </div>
                            <nav className="flex flex-col gap-1" aria-label="Menu mobile">
                                {NAV_LINKS.map((link) => (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        className={cn(
                                            "rounded-lg px-4 py-3 text-sm font-semibold transition-all",
                                            pathname === link.href
                                                ? "bg-hp-purple/10 text-hp-purple"
                                                : "text-hp-gray hover:bg-hp-purple/5 hover:text-hp-purple"
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                                {session ? (
                                    <>
                                        <Link
                                            href="/messages"
                                            className="rounded-lg px-4 py-3 text-sm font-semibold text-hp-gray hover:bg-hp-purple/5 hover:text-hp-purple transition-all flex items-center gap-2"
                                        >
                                            <MessageSquare className="h-4 w-4" /> Messages
                                        </Link>
                                        <Link
                                            href="/profil"
                                            className="rounded-lg px-4 py-3 text-sm font-semibold text-hp-gray hover:bg-hp-purple/5 hover:text-hp-purple transition-all flex items-center gap-2"
                                        >
                                            <User className="h-4 w-4" /> Mon profil
                                        </Link>
                                        <Link
                                            href="/profil/parametres"
                                            className="rounded-lg px-4 py-3 text-sm font-semibold text-hp-gray hover:bg-hp-purple/5 hover:text-hp-purple transition-all flex items-center gap-2"
                                        >
                                            <Settings className="h-4 w-4" /> Paramètres
                                        </Link>
                                        <Link
                                            href="/annonces/nouvelle"
                                            className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-hp-purple to-hp-orange px-4 py-3 text-sm font-bold text-white shadow-hp"
                                        >
                                            <Plus className="h-4 w-4" aria-hidden="true" />
                                            Proposer du matériel
                                        </Link>
                                        <button
                                            onClick={() => signOut({ callbackUrl: "/" })}
                                            className="mt-2 flex items-center justify-center gap-2 rounded-xl border border-red-200 dark:border-red-800 px-4 py-3 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <LogOut className="h-4 w-4" /> Déconnexion
                                        </button>
                                    </>
                                ) : (
                                    <Link
                                        href="/auth/login"
                                        className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-hp-purple to-hp-orange px-4 py-3 text-sm font-bold text-white shadow-hp"
                                    >
                                        <LogIn className="h-4 w-4" aria-hidden="true" />
                                        Connexion
                                    </Link>
                                )}
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
